
/* ==========================================================================
   Blusafe interactive manual renderer (shared across products).
   Reads window.OSP_MANUAL_DATA (set inline by the product manual template),
   builds the hero, install guides, troubleshooting, videos and search, and
   wires image zoom, progress tracking and analytics. Degrades gracefully if
   JS, the data, or individual images are unavailable.
   ========================================================================== */
(function () {
  'use strict';

  var root = document.querySelector('[data-osp-manual]');
  if (!root) return;

  /* ---------- analytics helper (also exposed globally) ---------- */
  window.blusafeManualEvent = window.blusafeManualEvent || function (name, params) {
    params = params || {};
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push(Object.assign({ event: name }, params));
    if (window.Shopify && window.Shopify.analytics && window.Shopify.analytics.publish) {
      try { window.Shopify.analytics.publish(name, params); } catch (e) {}
    }
  };
  function track(name, params) { window.blusafeManualEvent(name, params || {}); }

  /* ---------- config from data attributes ---------- */
  var cfg = {
    dataUrl: root.getAttribute('data-osp-data-url') || '',
    imageBase: root.getAttribute('data-osp-image-base') || '',
    assetBase: root.getAttribute('data-osp-asset-base') || '',
    handle: root.getAttribute('data-osp-content-handle') || 'origin-smart-plus',
    pdf: root.getAttribute('data-osp-pdf') || '',
    contact: root.getAttribute('data-osp-contact') || '',
    android: root.getAttribute('data-osp-android') || '',
    ios: root.getAttribute('data-osp-ios') || ''
  };
  // Inline data (preview build) takes priority over a fetch.
  var inlineData = window.OSP_MANUAL_DATA || null;

  /* ---------- small helpers ---------- */
  function $(sel, ctx) { return (ctx || root).querySelector(sel); }
  function el(tag, cls, html) {
    var n = document.createElement(tag);
    if (cls) n.className = cls;
    if (html != null) n.innerHTML = html;
    return n;
  }
  function esc(s) {
    return String(s == null ? '' : s)
      .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;').replace(/'/g, '&#39;');
  }
  function slug(s) { return String(s || '').toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, ''); }

  function resolveImg(src) {
    if (!src) return '';
    if (/^(https?:)?\/\//.test(src) || src.charAt(0) === '/') return src;
    if (src.indexOf('data:') === 0) return src;
    var base = cfg.imageBase || cfg.assetBase || '';
    if (base && base.charAt(base.length - 1) !== '/') base += '/';
    return base + src;
  }
  // Theme assets ship a single resolution; the lightbox reuses the same file.
  function hiresVariant(url) {
    return url;
  }

  /* ---------- figure (image with zoom + graceful fallback) ---------- */
  function figure(img, opts) {
    opts = opts || {};
    var url = resolveImg(img.src);
    var fig = el('figure', 'osp-figure' + (opts.single ? ' osp-figure--single' : ''));
    var btn = el('button');
    btn.type = 'button';
    btn.className = 'osp-image-button';
    btn.setAttribute('data-osp-lightbox', hiresVariant(url));
    btn.setAttribute('data-osp-lightbox-fallback', url);
    btn.setAttribute('data-osp-caption', img.alt || '');
    btn.setAttribute('aria-label', 'Zoom image: ' + (img.alt || 'manual image'));
    var im = el('img');
    im.src = url;
    im.alt = img.alt || '';
    im.loading = 'lazy';
    im.decoding = 'async';
    im.addEventListener('error', function () { fig.classList.add('osp-figure--missing'); });
    if (im.complete && im.naturalWidth === 0) fig.classList.add('osp-figure--missing');
    btn.appendChild(im);
    var ph = el('span', 'osp-image-placeholder', esc(img.alt || 'Image unavailable'));
    btn.appendChild(ph);
    fig.appendChild(btn);
    if (opts.caption && img.alt) fig.appendChild(el('figcaption', null, esc(img.alt)));
    return fig;
  }

  function figureGrid(images, opts) {
    if (!images || !images.length) return null;
    var grid = el('div', 'osp-figures' + (images.length === 1 ? ' osp-figures--single' : ''));
    images.forEach(function (img) { grid.appendChild(figure(img, opts || {})); });
    return grid;
  }

  function calloutNode(type, title, content) {
    var c = el('div', 'osp-callout osp-callout--' + type);
    if (title) c.appendChild(el('strong', null, esc(title)));
    c.appendChild(el('div', null, '<p>' + esc(content) + '</p>'));
    return c;
  }

  function listBlock(label, items, ordered) {
    if (!items || !items.length) return null;
    var frag = document.createDocumentFragment();
    frag.appendChild(el('p', 'osp-subhead', esc(label)));
    var list = el(ordered ? 'ol' : 'ul', 'osp-list');
    items.forEach(function (it) { list.appendChild(el('li', null, esc(it))); });
    frag.appendChild(list);
    return frag;
  }

  var APP_NAME = 'the app';
  var APP_LOGO = '';
  // Platform glyphs matching the UK manual buttons (Android robot / Apple mark).
  var GLYPH_ANDROID = '<svg viewBox="0 0 24 24" width="18" height="18" fill="currentColor" aria-hidden="true" focusable="false" style="display:block;flex:none;"><path d="M17.6 9.48l1.84-3.18c.16-.31.04-.69-.26-.85-.29-.15-.65-.06-.83.22l-1.88 3.24c-2.86-1.21-6.08-1.21-8.94 0L5.65 5.67c-.19-.29-.58-.38-.87-.2-.28.18-.37.54-.22.83L6.4 9.48C3.3 11.25 1.28 14.44 1 18h22c-.28-3.56-2.3-6.75-5.4-8.52zM7 15.25c-.69 0-1.25-.56-1.25-1.25s.56-1.25 1.25-1.25 1.25.56 1.25 1.25-.56 1.25-1.25 1.25zm10 0c-.69 0-1.25-.56-1.25-1.25s.56-1.25 1.25-1.25 1.25.56 1.25 1.25-.56 1.25-1.25 1.25z"/></svg>';
  var GLYPH_APPLE = '<svg viewBox="0 0 24 24" width="18" height="18" fill="currentColor" aria-hidden="true" focusable="false" style="display:block;flex:none;"><path d="M12.152 6.896c-.948 0-2.415-1.078-3.96-1.04-2.04.027-3.91 1.183-4.961 3.014-2.117 3.675-.546 9.103 1.519 12.09 1.013 1.454 2.208 3.09 3.792 3.03 1.52-.065 2.09-.987 3.935-.987 1.831 0 2.35.987 3.96.948 1.637-.026 2.676-1.48 3.676-2.948 1.156-1.688 1.636-3.325 1.662-3.415-.039-.013-3.182-1.221-3.22-4.857-.026-3.04 2.48-4.494 2.597-4.559-1.429-2.09-3.623-2.324-4.39-2.376-2-.156-3.675 1.09-4.61 1.09zM15.53 3.83c.843-1.012 1.4-2.427 1.245-3.83-1.207.052-2.662.805-3.532 1.818-.78.896-1.454 2.338-1.273 3.714 1.338.104 2.715-.688 3.559-1.701"/></svg>';
  function appButtonHtml(glyph, label) {
    var logo = APP_LOGO ? '<img class="osp-btn-logo" src="' + esc(resolveImg(APP_LOGO)) + '" alt="" width="20" height="20"> ' : '';
    return logo + glyph + ' ' + esc(APP_NAME) + ' ' + label;
  }
  function appLinksNode() {
    var wrap = el('div', 'osp-app-links');
    wrap.setAttribute('aria-label', 'Download the ' + APP_NAME + ' app');
    if (cfg.android) {
      var a = el('a', 'osp-button osp-button--primary', appButtonHtml(GLYPH_ANDROID, 'for Android'));
      a.href = cfg.android; a.target = '_blank'; a.rel = 'noopener';
      a.setAttribute('data-osp-event', 'app_link_click'); a.setAttribute('data-app-store', 'google_play');
      wrap.appendChild(a);
    }
    if (cfg.ios) {
      var i = el('a', 'osp-button osp-button--secondary', appButtonHtml(GLYPH_APPLE, 'for iPhone'));
      i.href = cfg.ios; i.target = '_blank'; i.rel = 'noopener';
      i.setAttribute('data-osp-event', 'app_link_click'); i.setAttribute('data-app-store', 'ios_app_store');
      wrap.appendChild(i);
    }
    return wrap;
  }

  /* ---------- hero (built from data.manual so one shell serves all products) ---------- */
  function renderHero(data) {
    var m = (data && data.manual) || {};
    var title = $('[data-osp-hero-title]');
    if (title && (m.hero_title || m.title)) title.textContent = m.hero_title || m.title;

    var lead = $('[data-osp-hero-lead]');
    if (lead) {
      if (m.lead) { lead.textContent = m.lead; lead.hidden = false; }
      else lead.hidden = true;
    }

    var stats = $('[data-osp-hero-stats]');
    if (stats) {
      var rows = [];
      if (m.manual_version) rows.push(['Manual version', m.manual_version]);
      if (m.primary_app) rows.push(['Correct app', m.primary_app]);
      if (m.install_time_estimate) rows.push(['Install time', m.install_time_estimate]);
      if (rows.length) {
        stats.innerHTML = rows.map(function (r) {
          return '<div><dt>' + esc(r[0]) + '</dt><dd>' + esc(r[1]) + '</dd></div>';
        }).join('');
        stats.hidden = false;
      }
    }

    var unlocks = $('[data-osp-unlocks]');
    if (unlocks && m.unlock_methods && m.unlock_methods.length) {
      var ul = el('ul', 'osp-unlocks');
      ul.setAttribute('aria-label', 'Ways to unlock');
      m.unlock_methods.forEach(function (u) {
        var li = el('li');
        if (u.icon) {
          var ic = el('img');
          ic.src = resolveImg(u.icon); ic.alt = ''; ic.width = 42; ic.height = 42; ic.loading = 'lazy';
          li.appendChild(ic);
        }
        li.appendChild(el('span', null, esc(u.label)));
        ul.appendChild(li);
      });
      unlocks.innerHTML = '';
      unlocks.appendChild(ul);
      if (m.unlock_note) {
        var note = el('p', null, esc(m.unlock_note));
        note.style.cssText = 'margin:.45rem 0 0;color:var(--osp-muted);font-size:.78rem;';
        unlocks.appendChild(note);
      }
      unlocks.hidden = false;
    }

    var warn = $('[data-osp-hero-warning]');
    if (warn && m.hero_warning && m.hero_warning.body) {
      warn.innerHTML = '';
      var wc = calloutNode('warning', m.hero_warning.title || 'Important', m.hero_warning.body);
      if (m.hero_warning.logo) {
        wc.className = 'osp-callout osp-callout--warning osp-callout--app';
        var lg = el('img', 'osp-ttlock-logo');
        lg.src = resolveImg(m.hero_warning.logo); lg.alt = (m.primary_app || 'App') + ' logo';
        lg.width = 46; lg.height = 46; lg.loading = 'lazy';
        wc.insertBefore(lg, wc.firstChild);
      }
      warn.appendChild(wc);
      warn.hidden = false;
    }

    var links = $('[data-osp-app-links]');
    if (links && (cfg.android || cfg.ios)) {
      links.innerHTML = '';
      links.appendChild(appLinksNode());
      links.hidden = false;
    }

    var videoLink = $('[data-osp-video-link]');
    if (videoLink && !(data.videos && data.videos.length)) videoLink.style.display = 'none';

    var suggest = $('[data-osp-search-suggest]');
    if (suggest) {
      if (m.popular_searches && m.popular_searches.length) {
        suggest.innerHTML = '<span>Popular:</span>';
        m.popular_searches.forEach(function (q) {
          var chip = el('button', 'osp-chip', esc(q));
          chip.type = 'button';
          suggest.appendChild(chip);
        });
        suggest.hidden = false;
      } else {
        suggest.hidden = true;
      }
    }

    var panel = $('[data-osp-hero-panel]');
    if (panel) {
      var html = '';
      if (m.checklist && m.checklist.length) {
        html += '<h2>' + esc(m.checklist_title || 'Before you touch the door') + '</h2><ul class="osp-checklist">';
        m.checklist.forEach(function (c) { html += '<li>' + esc(c) + '</li>'; });
        html += '</ul>';
      }
      if (m.hero_table && m.hero_table.rows && m.hero_table.rows.length) {
        if (html) html += '<hr>';
        if (m.hero_table.title) html += '<h3>' + esc(m.hero_table.title) + '</h3>';
        html += '<div class="osp-table-wrap"><table class="osp-table">';
        if (m.hero_table.head && m.hero_table.head.length) {
          html += '<thead><tr>' + m.hero_table.head.map(function (h) { return '<th scope="col">' + esc(h) + '</th>'; }).join('') + '</tr></thead>';
        }
        html += '<tbody>' + m.hero_table.rows.map(function (r) {
          return '<tr>' + r.map(function (c) { return '<td>' + esc(c) + '</td>'; }).join('') + '</tr>';
        }).join('') + '</tbody></table></div>';
      }
      if (m.quick_specs) {
        var keys = Object.keys(m.quick_specs);
        if (keys.length) {
          if (html) html += '<hr>';
          html += '<h3>Quick specs</h3><ul class="osp-specs">';
          keys.forEach(function (k) {
            html += '<li><span class="k">' + esc(k) + '</span><span class="v">' + esc(m.quick_specs[k]) + '</span></li>';
          });
          html += '</ul>';
        }
      }
      if (html) { panel.innerHTML = html; panel.hidden = false; }
    }

    var tsLead = $('[data-osp-troubleshooting-lead]');
    if (tsLead && m.troubleshooting_lead) tsLead.textContent = m.troubleshooting_lead;

    var search = $('[data-osp-search]');
    if (search && m.search_placeholder) search.placeholder = m.search_placeholder;

    var supportList = $('[data-osp-support-list]');
    var evidence = data && data.support_evidence_default;
    if (supportList && evidence && evidence.length) {
      supportList.innerHTML = evidence.map(function (it) { return '<li>' + esc(it) + '</li>'; }).join('');
    }

    var contact = $('[data-osp-contact-link]');
    if (contact && cfg.contact) contact.href = cfg.contact;
  }

  /* ---------- progress persistence ---------- */
  var store = {
    key: 'osp-progress:' + cfg.handle,
    read: function () { try { return JSON.parse(localStorage.getItem(this.key)) || {}; } catch (e) { return {}; } },
    write: function (v) { try { localStorage.setItem(this.key, JSON.stringify(v)); } catch (e) {} }
  };
  var progress = store.read();
  var totalSteps = 0;
  var startedGuides = {};

  /* ---------- render guides ---------- */
  function renderGuides(guides) {
    var mount = $('[data-osp-guides]');
    if (!mount) return;
    mount.innerHTML = '';
    guides.forEach(function (g, gi) {
      totalSteps += (g.steps || []).length;
      var det = el('details', 'osp-guide');
      det.id = g.handle;
      if (g.priority) det.setAttribute('data-priority', g.priority);

      var sum = el('summary', 'osp-guide__summary');
      sum.appendChild(el('span', 'osp-guide__num', String(gi + 1)));
      var head = el('div', 'osp-guide__head');
      head.appendChild(el('h3', null, esc(g.title)));
      var meta = el('div', 'osp-guide__meta');
      if (g.category) meta.appendChild(el('span', 'osp-pill', esc(g.category)));
      if (g.estimated_time) meta.appendChild(el('span', 'osp-pill', esc(g.estimated_time)));
      if (g.difficulty) meta.appendChild(el('span', 'osp-pill', esc(g.difficulty)));
      var prog = el('span', 'osp-pill osp-guide__progress');
      meta.appendChild(prog);
      head.appendChild(meta);
      sum.appendChild(head);
      sum.appendChild(el('span', 'osp-guide__chevron', '⌄'));
      det.appendChild(sum);

      var body = el('div', 'osp-guide__body');
      if (g.summary) body.appendChild(el('p', 'osp-guide__summary-text', esc(g.summary)));

      var grid = figureGrid(g.images, { caption: true });
      if (grid) body.appendChild(grid);

      (g.warnings || []).forEach(function (w) { body.appendChild(calloutNode('warning', 'Warning', w)); });
      (g.tips || []).forEach(function (t) { body.appendChild(calloutNode('info', 'Tip', t)); });
      (g.notes || []).forEach(function (n) { body.appendChild(calloutNode('note', 'Note', n)); });

      if (g.app_links) body.appendChild(appLinksNode());

      // steps with checkboxes
      if (g.steps && g.steps.length) {
        body.appendChild(el('p', 'osp-subhead', 'Steps'));
        var ol = el('ol', 'osp-steps');
        g.steps.forEach(function (s, si) {
          var li = el('li');
          var id = g.handle + ':' + si;
          var cb = el('input');
          cb.type = 'checkbox'; cb.className = 'osp-step-check'; cb.id = 'cb-' + slug(id);
          cb.setAttribute('data-step-key', id);
          cb.setAttribute('aria-label', 'Mark step ' + (si + 1) + ' complete');
          if (progress[id]) { cb.checked = true; li.classList.add('is-done'); }
          var lab = el('label', 'osp-step-text', esc(s));
          lab.setAttribute('for', cb.id);
          li.appendChild(cb);
          li.appendChild(lab);
          ol.appendChild(li);
        });
        body.appendChild(ol);
      }

      if (g.expected_result) body.appendChild(calloutNode('success', 'Expected result', g.expected_result));
      var cm = listBlock('Common mistakes', g.common_mistakes);
      if (cm) body.appendChild(cm);

      body.appendChild(helpfulNode('guide', g.handle));
      det.appendChild(body);
      mount.appendChild(det);

      det.addEventListener('toggle', function () {
        if (det.open && !startedGuides[g.handle]) {
          startedGuides[g.handle] = true;
          track('guide_start', { content_handle: cfg.handle, guide: g.handle, guide_title: g.title });
        }
      });

      updateGuideProgress(det, g);
    });
  }

  function updateGuideProgress(det, g) {
    var steps = g.steps || [];
    if (!steps.length) { var p0 = $('.osp-guide__progress', det); if (p0) p0.style.display = 'none'; return; }
    var done = 0;
    steps.forEach(function (s, si) { if (progress[g.handle + ':' + si]) done++; });
    var pill = $('.osp-guide__progress', det);
    if (pill) {
      pill.textContent = done + '/' + steps.length;
      pill.classList.toggle('osp-pill--done', done === steps.length);
    }
  }

  /* ---------- render troubleshooting ---------- */
  function renderIssues(issues) {
    var mount = $('[data-osp-issues]');
    if (!mount) return;
    if (!issues || !issues.length) {
      var sec = mount.closest('.osp-section');
      if (sec) sec.style.display = 'none';
      var fix = $('[data-osp-fix-link]');
      if (fix) fix.style.display = 'none';
      return;
    }
    mount.innerHTML = '';
    issues.forEach(function (t) {
      var det = el('details', 'osp-issue');
      det.id = t.handle;
      if (t.priority) det.setAttribute('data-priority', t.priority);
      var sum = el('summary', 'osp-issue__summary');
      var h = el('div');
      if (t.category) h.appendChild(el('span', 'osp-issue__cat', esc(t.category) + (t.priority === 'high' ? ' • common' : '')));
      h.appendChild(el('h3', null, esc(t.title)));
      sum.appendChild(h);
      sum.appendChild(el('span', 'osp-issue__chevron', '⌄'));
      det.appendChild(sum);

      var body = el('div', 'osp-issue__body');
      if (t.explanation) body.appendChild(calloutNode('info', 'Why this happens', t.explanation));
      var sym = listBlock('Symptoms', t.symptoms);
      if (sym) body.appendChild(sym);
      var qc = listBlock('Quick checks', t.quick_checks);
      if (qc) body.appendChild(qc);
      var lc = listBlock('Likely causes', t.likely_causes);
      if (lc) body.appendChild(lc);
      var fx = listBlock('Fix steps', t.fix_steps, true);
      if (fx) body.appendChild(fx);
      if (t.app_links) body.appendChild(appLinksNode());

      if (t.related_guides && t.related_guides.length) {
        body.appendChild(el('p', 'osp-subhead', 'Related guides'));
        var rel = el('div', 'osp-related');
        t.related_guides.forEach(function (rg) {
          var a = el('a', null, '→ ' + esc(guideTitle(rg)));
          a.href = '#' + rg;
          a.setAttribute('data-osp-jump', rg);
          rel.appendChild(a);
        });
        body.appendChild(rel);
      }

      var ev = listBlock('Send this to support', t.support_evidence);
      if (ev) body.appendChild(ev);

      body.appendChild(helpfulNode('troubleshooting', t.handle));
      det.appendChild(body);
      mount.appendChild(det);

      var viewed = false;
      det.addEventListener('toggle', function () {
        if (det.open && !viewed) {
          viewed = true;
          track('troubleshooting_view', { content_handle: cfg.handle, issue: t.handle, issue_title: t.title });
        }
      });
    });
  }

  var GUIDE_TITLES = {};
  function guideTitle(handle) { return GUIDE_TITLES[handle] || handle; }

  /* ---------- videos ---------- */
  function videoCard(v, feature) {
    var card = el('div', 'osp-video' + (feature ? ' osp-video--feature' : ''));
    var frame = el('div', 'osp-video__frame');
    var ifr = el('iframe');
    ifr.src = 'https://www.youtube-nocookie.com/embed/' + encodeURIComponent(v.youtube_id);
    ifr.title = v.title || 'Video';
    ifr.loading = 'lazy';
    ifr.setAttribute('allow', 'accelerometer; clipboard-write; encrypted-media; gyroscope; picture-in-picture');
    ifr.setAttribute('allowfullscreen', '');
    frame.appendChild(ifr);
    card.appendChild(frame);
    var b = el('div', 'osp-video__body');
    b.appendChild(el('h3', null, esc(v.title)));
    if (v.description) b.appendChild(el('p', null, esc(v.description)));
    card.appendChild(b);
    return card;
  }
  // Featured "Video guide" at the top of the page (first/primary video).
  function renderFeaturedVideo(videos) {
    var mount = $('[data-osp-featured-video]');
    if (!mount) return false;
    if (!videos || !videos.length) { var sec = mount.closest('.osp-section'); if (sec) sec.style.display = 'none'; return false; }
    mount.innerHTML = '';
    mount.appendChild(videoCard(videos[0], true));
    return true;
  }
  function renderVideos(videos) {
    var mount = $('[data-osp-videos]');
    if (!mount) return;
    var hasFeatured = !!$('[data-osp-featured-video]');
    var list = (videos || []).slice(hasFeatured ? 1 : 0);
    if (!list.length) { var sec = mount.closest('.osp-section'); if (sec) sec.style.display = 'none'; return; }
    mount.innerHTML = '';
    list.forEach(function (v) { mount.appendChild(videoCard(v, false)); });
  }

  /* ---------- pre-written support emails ---------- */
  function renderTemplates(templates) {
    var mount = $('[data-osp-templates]');
    if (!mount) return;
    if (!templates || !templates.length) { var sec = mount.closest('.osp-section'); if (sec) sec.style.display = 'none'; return; }
    var addr = /@/.test(cfg.contact) ? cfg.contact : (inlineData && inlineData.support_email) || '';
    mount.innerHTML = '';
    templates.forEach(function (tpl) {
      var det = el('details', 'osp-template');
      var sum = el('summary');
      sum.appendChild(el('span', null, esc(tpl.title)));
      sum.appendChild(el('span', 'osp-issue__chevron', '⌄'));
      det.appendChild(sum);
      var body = el('div', 'osp-template__body');
      if (addr || tpl.subject) {
        body.appendChild(el('p', 'osp-template__subject',
          (addr ? 'To: ' + esc(addr) : '') +
          (tpl.subject ? (addr ? '<br>' : '') + 'Subject: ' + esc(tpl.subject) : '')));
      }
      var pre = el('pre');
      pre.textContent = tpl.body || '';
      body.appendChild(pre);
      var actions = el('div', 'osp-actions');
      if (addr) {
        var mail = el('a', 'osp-button osp-button--primary', 'Open in your email app');
        mail.href = 'mailto:' + addr + '?subject=' + encodeURIComponent(tpl.subject || '') + '&body=' + encodeURIComponent(tpl.body || '');
        mail.setAttribute('data-osp-event', 'support_email_open');
        actions.appendChild(mail);
      }
      var copy = el('button', 'osp-button osp-button--secondary osp-copy', 'Copy email text');
      copy.type = 'button';
      copy.setAttribute('data-osp-copy', '');
      actions.appendChild(copy);
      body.appendChild(actions);
      det.appendChild(body);
      mount.appendChild(det);
    });
  }

  /* ---------- table of contents + scrollspy ---------- */
  function renderToc(data) {
    var mount = $('[data-osp-toc]');
    if (!mount) return;
    var guides = data.guides || [];
    var issues = data.troubleshooting || [];
    var html = '<h2>On this page</h2><ul class="osp-toc__list">';
    if (guides.length) {
      html += '<li class="osp-toc__group">Install &amp; setup</li>';
      guides.forEach(function (g) {
        html += '<li><a class="osp-toc__link" href="#' + esc(g.handle) + '" data-osp-jump="' + esc(g.handle) + '">' + esc(g.title) + '</a></li>';
      });
    }
    if (issues.length) {
      html += '<li class="osp-toc__group">Troubleshooting</li>';
      issues.forEach(function (t) {
        html += '<li><a class="osp-toc__link" href="#' + esc(t.handle) + '" data-osp-jump="' + esc(t.handle) + '">' + esc(t.title) + '</a></li>';
      });
    }
    html += '</ul>';
    mount.innerHTML = html;
  }

  function setupScrollSpy() {
    var links = [].slice.call(root.querySelectorAll('.osp-toc__link'));
    if (!links.length || !('IntersectionObserver' in window)) return;
    var map = {};
    links.forEach(function (l) { map[l.getAttribute('data-osp-jump')] = l; });
    var obs = new IntersectionObserver(function (entries) {
      entries.forEach(function (e) {
        if (e.isIntersecting) {
          links.forEach(function (l) { l.classList.remove('is-active'); });
          var active = map[e.target.id];
          if (active) active.classList.add('is-active');
        }
      });
    }, { rootMargin: '-20% 0px -70% 0px' });
    Object.keys(map).forEach(function (id) { var n = document.getElementById(id); if (n) obs.observe(n); });
  }

  /* ---------- progress bar ---------- */
  function renderProgressBar() {
    var mount = $('[data-osp-progress]');
    if (!mount) return;
    mount.innerHTML =
      '<div class="osp-progress__label"><span data-osp-progress-text>Installation progress</span>' +
      '<button type="button" class="osp-progress__reset" data-osp-progress-reset>Reset</button></div>' +
      '<div class="osp-progressbar"><span data-osp-progress-fill></span></div>';
    updateProgressBar();
  }
  function countDone() { var n = 0; Object.keys(progress).forEach(function (k) { if (progress[k]) n++; }); return n; }
  function updateProgressBar() {
    var fill = $('[data-osp-progress-fill]'); var txt = $('[data-osp-progress-text]');
    if (!fill) return;
    var done = countDone();
    var pct = totalSteps ? Math.round((done / totalSteps) * 100) : 0;
    fill.style.width = pct + '%';
    if (txt) txt.textContent = 'Installation progress — ' + done + ' of ' + totalSteps + ' steps (' + pct + '%)';
  }

  /* ---------- helpful widget ---------- */
  function helpfulNode(type, handle) {
    var w = el('div', 'osp-helpful');
    w.setAttribute('data-osp-helpful', type + ':' + handle);
    w.innerHTML =
      '<span>Was this helpful?</span>' +
      '<button type="button" data-helpful="yes">Yes</button>' +
      '<button type="button" data-helpful="no">No</button>' +
      '<span class="osp-helpful__thanks">Thanks for the feedback.</span>';
    return w;
  }

  /* ---------- search ---------- */
  function setupSearch(index) {
    var input = $('[data-osp-search]');
    var out = $('[data-osp-search-results]');
    if (!input || !out) return;
    var debounce;

    function render() {
      var q = input.value.trim().toLowerCase();
      out.innerHTML = '';
      if (q.length < 2) return;
      var terms = q.split(/\s+/);
      var matches = index.filter(function (row) {
        var hay = (row.title + ' ' + row.category + ' ' + row.summary + ' ' + row.keywords).toLowerCase();
        return terms.every(function (t) { return hay.indexOf(t) !== -1; });
      }).slice(0, 12);

      clearTimeout(debounce);
      debounce = setTimeout(function () {
        track('manual_search', { content_handle: cfg.handle, search_query: q, result_count: matches.length });
        if (!matches.length) track('manual_search_no_results', { content_handle: cfg.handle, search_query: q });
      }, 600);

      if (!matches.length) {
        out.appendChild(el('div', 'osp-search-result', '<strong>No results found</strong><small>Try a different word — e.g. install, fingerprint, passcode, app, reset — or contact support.</small>'));
        return;
      }
      matches.forEach(function (m) {
        var a = el('a', 'osp-search-result');
        a.href = '#' + m.anchor;
        a.setAttribute('data-osp-jump', m.anchor);
        a.setAttribute('data-osp-search-result', m.title);
        a.innerHTML = '<small>' + esc(m.type === 'troubleshooting' ? 'Fix' : m.category) + '</small><strong>' +
          highlight(m.title, terms) + '</strong>' + (m.summary ? '<span>' + highlight(m.summary, terms) + '</span>' : '');
        out.appendChild(a);
      });
    }
    input.addEventListener('input', render);
    input.addEventListener('keydown', function (e) {
      if (e.key === 'Enter') {
        var first = out.querySelector('a[data-osp-jump]');
        if (first) { e.preventDefault(); first.click(); }
      }
    });
    root.querySelectorAll('.osp-chip').forEach(function (chip) {
      chip.addEventListener('click', function () {
        input.value = chip.textContent;
        render();
        input.focus();
      });
    });
  }
  function highlight(text, terms) {
    var safe = esc(text);
    terms.forEach(function (t) {
      if (t.length < 2) return;
      var re = new RegExp('(' + t.replace(/[.*+?^${}()|[\]\\]/g, '\\$&') + ')', 'ig');
      safe = safe.replace(re, '<mark>$1</mark>');
    });
    return safe;
  }

  /* ---------- jump to + open target ---------- */
  function jumpTo(id) {
    var node = document.getElementById(id);
    if (!node) return;
    if (node.tagName === 'DETAILS') node.open = true;
    node.scrollIntoView({ behavior: 'smooth', block: 'start' });
    node.setAttribute('tabindex', '-1');
    try { node.focus({ preventScroll: true }); } catch (e) {}
  }

  /* ---------- lightbox ---------- */
  function openLightbox(src, caption, fallback) {
    if (!src) return;
    var lb = el('div', 'osp-lightbox');
    lb.setAttribute('role', 'dialog');
    lb.setAttribute('aria-modal', 'true');
    var fig = el('figure');
    var img = el('img'); img.alt = caption || 'Manual image';
    if (fallback && fallback !== src) {
      img.addEventListener('error', function once() { img.removeEventListener('error', once); img.src = fallback; });
    }
    img.src = src;
    fig.appendChild(img);
    if (caption) fig.appendChild(el('figcaption', null, esc(caption)));
    var close = el('button', 'osp-lightbox__close', 'Close ✕'); close.type = 'button';
    lb.appendChild(close); lb.appendChild(fig);
    function done() { lb.remove(); document.removeEventListener('keydown', onKey); }
    function onKey(e) { if (e.key === 'Escape') done(); }
    lb.addEventListener('click', function (e) { if (e.target === lb || e.target === close) done(); });
    document.addEventListener('keydown', onKey);
    document.body.appendChild(lb);
    close.focus();
  }

  /* ---------- delegated events ---------- */
  function wireEvents() {
    root.addEventListener('click', function (e) {
      var lb = e.target.closest('[data-osp-lightbox]');
      if (lb) { e.preventDefault(); openLightbox(lb.getAttribute('data-osp-lightbox'), lb.getAttribute('data-osp-caption'), lb.getAttribute('data-osp-lightbox-fallback')); return; }

      var jump = e.target.closest('[data-osp-jump]');
      if (jump) {
        e.preventDefault();
        var id = jump.getAttribute('data-osp-jump');
        if (jump.matches('[data-osp-search-result]')) track('manual_search_result_click', { content_handle: cfg.handle, title: jump.getAttribute('data-osp-search-result') });
        jumpTo(id);
        return;
      }

      var ev = e.target.closest('[data-osp-event]');
      if (ev) {
        var params = { content_handle: cfg.handle };
        if (ev.getAttribute('data-app-store')) params.app_store = ev.getAttribute('data-app-store');
        track(ev.getAttribute('data-osp-event'), params);
      }

      var copy = e.target.closest('[data-osp-copy]');
      if (copy) {
        var wrapEl = copy.closest('.osp-template__body');
        var pre = wrapEl && wrapEl.querySelector('pre');
        if (pre) copyText(pre.textContent, copy);
        track('support_cta_click', { content_handle: cfg.handle, action: 'copy_template' });
      }

      var help = e.target.closest('[data-osp-helpful] [data-helpful]');
      if (help) {
        var widget = help.closest('[data-osp-helpful]');
        widget.setAttribute('data-answered', '');
        var ref = widget.getAttribute('data-osp-helpful').split(':');
        track('was_this_helpful', { content_handle: cfg.handle, section_type: ref[0], section: ref[1], helpful: help.getAttribute('data-helpful') });
      }
    });

    // step checkboxes -> progress
    root.addEventListener('change', function (e) {
      var cb = e.target.closest('.osp-step-check');
      if (!cb) return;
      var key = cb.getAttribute('data-step-key');
      progress[key] = cb.checked;
      store.write(progress);
      cb.closest('li').classList.toggle('is-done', cb.checked);
      var det = cb.closest('.osp-guide');
      var gHandle = key.split(':')[0];
      var g = GUIDE_BY_HANDLE[gHandle];
      if (det && g) updateGuideProgress(det, g);
      updateProgressBar();
      if (cb.checked) track('step_view', { content_handle: cfg.handle, guide: gHandle, step: Number(key.split(':')[1]) + 1 });
      if (g) {
        var allDone = (g.steps || []).every(function (s, si) { return progress[gHandle + ':' + si]; });
        if (allDone && g.steps && g.steps.length) track('guide_complete', { content_handle: cfg.handle, guide: gHandle, guide_title: g.title });
      }
    });

    var resetBtn = $('[data-osp-progress-reset]');
    if (resetBtn) resetBtn.addEventListener('click', function () {
      progress = {}; store.write(progress);
      root.querySelectorAll('.osp-step-check').forEach(function (cb) { cb.checked = false; cb.closest('li').classList.remove('is-done'); });
      root.querySelectorAll('.osp-guide').forEach(function (det) { var g = GUIDE_BY_HANDLE[det.id]; if (g) updateGuideProgress(det, g); });
      updateProgressBar();
    });
  }

  function copyText(text, btn) {
    function ok() { if (btn) { btn.setAttribute('data-copied', ''); setTimeout(function () { btn.removeAttribute('data-copied'); }, 2000); } }
    if (navigator.clipboard && navigator.clipboard.writeText) {
      navigator.clipboard.writeText(text).then(ok).catch(function () { legacy(); });
    } else { legacy(); }
    function legacy() {
      var ta = document.createElement('textarea'); ta.value = text; ta.style.position = 'fixed'; ta.style.opacity = '0';
      document.body.appendChild(ta); ta.select();
      try { document.execCommand('copy'); ok(); } catch (e) {}
      ta.remove();
    }
  }

  /* ---------- back to top ---------- */
  function setupBackToTop() {
    var btn = $('[data-osp-totop]');
    if (!btn) return;
    window.addEventListener('scroll', function () {
      btn.classList.toggle('is-visible', window.scrollY > 700);
    }, { passive: true });
    btn.addEventListener('click', function () { window.scrollTo({ top: 0, behavior: 'smooth' }); });
  }

  /* ---------- boot ---------- */
  var GUIDE_BY_HANDLE = {};

  function build(data) {
    if (!data) return;
    var m = data.manual || {};
    if (m.primary_app) APP_NAME = m.app_name || m.primary_app;
    APP_LOGO = m.app_logo || (m.hero_warning && m.hero_warning.logo) || '';
    // Variant manuals (e.g. Breeze Plus A/C) override the page-level config so
    // progress, analytics, the PDF button and the product button follow the variant.
    if (m.content_handle) {
      cfg.handle = m.content_handle;
      store.key = 'osp-progress:' + cfg.handle;
      progress = store.read();
    }
    if (m.pdf_file) cfg.pdf = resolveImg(m.pdf_file);
    var pdfBtn = root.querySelector('[data-osp-event="pdf_download"]');
    if (pdfBtn && cfg.pdf) pdfBtn.href = cfg.pdf;
    var prodBtn = root.querySelector('[data-osp-event="product_page_click"]');
    if (prodBtn && m.product_url) prodBtn.href = m.product_url;
    // Manuals without their own video data fall back to the page's Shopify
    // video tutorials (window.OSP_EXTRA_VIDEOS, set by the Liquid template).
    if ((!data.videos || !data.videos.length) && Array.isArray(window.OSP_EXTRA_VIDEOS)) {
      data.videos = window.OSP_EXTRA_VIDEOS.map(function (v) {
        var idMatch = /(?:youtube\.com\/(?:watch\?v=|shorts\/|embed\/)|youtu\.be\/)([\w-]{6,})/.exec(v.url || '');
        return idMatch ? { title: v.title || 'Video guide', url: v.url, youtube_id: idMatch[1], description: v.description || '' } : null;
      }).filter(Boolean);
    }
    if (m.app_links) {
      if (!cfg.android && m.app_links.android_google_play) cfg.android = m.app_links.android_google_play;
      if (!cfg.ios && m.app_links.ios_app_store) cfg.ios = m.app_links.ios_app_store;
    }
    (data.guides || []).forEach(function (g) { GUIDE_TITLES[g.handle] = g.title; GUIDE_BY_HANDLE[g.handle] = g; });
    renderHero(data);
    renderToc(data);
    renderGuides(data.guides || []);
    renderIssues(data.troubleshooting || []);
    renderFeaturedVideo(data.videos || []);
    renderVideos(data.videos || []);
    renderTemplates(data.support_templates || []);
    renderProgressBar();
    setupSearch(data.search_index || buildIndex(data));
    wireEvents();
    setupScrollSpy();
    setupBackToTop();

    track('manual_view', { content_type: 'manual', content_handle: cfg.handle, product_family: m.product_family || cfg.handle });

    // open a deep-linked section
    if (location.hash) { var id = location.hash.slice(1); setTimeout(function () { jumpTo(id); }, 200); }
  }

  function buildIndex(data) {
    var idx = [];
    (data.guides || []).forEach(function (g) {
      idx.push({ title: g.title, type: 'guide', category: g.category, anchor: g.handle, summary: g.summary || '', keywords: (g.steps || []).join(' ') });
    });
    (data.troubleshooting || []).forEach(function (t) {
      idx.push({ title: t.title, type: 'troubleshooting', category: t.category, anchor: t.handle, summary: (t.symptoms || [''])[0], keywords: (t.fix_steps || []).join(' ') });
    });
    return idx;
  }

  function fail(msg) {
    var mount = $('[data-osp-guides]');
    if (mount) mount.innerHTML = '<div class="osp-callout osp-callout--warning"><strong>Manual content could not load.</strong><div><p>' +
      esc(msg) + ' Please use the PDF fallback at the top of the page.</p></div></div>';
  }

  if (inlineData) {
    build(inlineData);
  } else if (cfg.dataUrl) {
    fetch(cfg.dataUrl, { credentials: 'same-origin' })
      .then(function (r) { if (!r.ok) throw new Error('HTTP ' + r.status); return r.json(); })
      .then(build)
      .catch(function (err) { fail('Could not load the manual data file.'); });
  } else {
    fail('No data source configured.');
  }
})();




